import {types, flow, getEnv} from "mobx-state-tree";
import {addTimestampToFileName, getFileFormat} from "../../utils/common";
import Notification from "../../utils/Notification";
import {DimensionsMultiSelectState} from "../../components/elements/dimensions/multi-select/dimensionsMultiSelectState";
import {TagSelectState} from "../../components/elements/tags/TagSelectState";

export const MediaFile = types.model('MediaFile',{
    uuid: types.identifier,
    usage: types.optional(types.string,"content"),
    status: types.optional(types.string,""),
    s3bucketUrl: types.maybe(types.string),
    codeUUIDs: types.optional(types.frozen(), []),
    name: types.maybe(types.string),
    label:types.maybe(types.string),
    mediaType: types.maybe(types.string), // for acceptable category changing
    nameIsInvalid: types.optional(types.boolean, false),
    file: types.optional(types.string, ''),
    fileIsCorrectSize: types.optional(types.boolean, true),
    fileIsCorrectType: types.optional(types.boolean, true),
    fileFormat: types.optional(types.string, ''),
    imageUrl: types.optional(types.string, ''),
    liveLinks:types.maybe(types.frozen()),  // {'original': int, 'formatType': int,...} // formatsType: small, medium, large, thumbnail
    singleUseFile: types.optional(types.boolean, false),
    fileForS3Upload: types.optional(types.frozen(),{}),
    mediaFileUUID: types.optional(types.string, ''),
    dimensionsMultiSelectStore: types.maybeNull(types.late(() => types.reference(DimensionsMultiSelectState))),
    tagSelectStore: types.maybe(types.late(() => types.reference(TagSelectState))),
    isExclusivelyGroupOwned: types.optional(types.boolean, true),
  //TODO: complete object

}).volatile(self => ({
  notifySelect: types.f,
  notifyDesignerDeleteKey: types.f,
  notifyUploadStatus: types.f,
  uploadIntervalId: null,
  cancelUploads: false
})).actions((self) => ({
  setConsumer(f){
    self.notifySelect = f
  },
  setDeleteKeyConsumer(f){
    self.notifyDesignerDeleteKey = f
  },
  setUploadStatusConsumer(f){
    self.notifyUploadStatus = f
  },
  validateMediaFileModel() {
    let isValid = true;
    if (self.name === "") {
      self.nameIsInvalid = true;
      isValid = false;
    }
    return isValid;
  },
  setFileFormat(fileFormat) {
    self.fileFormat = fileFormat
  },
  isInputInvalid(inputType) {
    return self[`${inputType}IsInvalid`]
  },
  updateName(e) {
    self.name = e
  },

  updateMediaFile(mediaFile){
    self.usage = mediaFile.usage? mediaFile.usage : self.usage
    self.status = mediaFile.status? mediaFile.status : self.status
    self.s3bucketUrl = mediaFile.s3bucketUrl? mediaFile.s3bucketUrl : self.s3bucketUrl
    self.name = mediaFile.name? mediaFile.name : self.name
    self.label = mediaFile.label? mediaFile.label : self.label
    self.mediaType = mediaFile.mediaType? mediaFile.mediaType : self.mediaType
    self.file = mediaFile.file? mediaFile.file : self.file
    self.fileFormat = mediaFile.fileFormat? mediaFile.fileFormat : self.fileFormat
    self.liveLinks = mediaFile.liveLinks? mediaFile.liveLinks : self.liveLinks
    self.singleUseFile = mediaFile.singleUseFile ?? self.singleUseFile;

  },
  updateInput(inputType, e) {
    self[inputType] = e.target.value
    if("imageUrl" === inputType){
      self.file = ''
      //check to make sure the url is valid
    }
  },
  setFileForS3Upload(file,singleUseFile){
    //self.setMediaFile(file)
    self.name = file.name
    self.fileForS3Upload = file
    self.singleUseFile = singleUseFile
  },
  setMediaFile(mediaFile, experienceFieldName) {
    //self.addExperienceFieldAndMediaFileUuid(mediaFile?.uuid, experienceFieldName)
    if (mediaFile?.uuid) {
      if (mediaFile.liveLinks?.original) {
        //self.updateExperienceData({[experienceFieldName]: mediaFile.liveLinks?.original})
      } else {
        new Notification()
          .setType("info")
          .setTitle(`File: ${mediaFile.label}`)
          .setMessage("Is processing. Try again in a few minutes")
          .send();
        //self.updateExperienceData({[experienceFieldName]: ""})
      }
      self.mediaFileUUID = mediaFile.uuid
    } else if (mediaFile?.name) {
      //self.updateExperienceData({[experienceFieldName]: ""})
      self.mediaFile = MediaFile.create({
        uuid: mediaFile?.name,
        name: mediaFile?.name,
      })
    }
  },
  addExperienceFieldAndMediaFileUuid(uuid,experienceFieldName) {
    if(uuid && experienceFieldName){ //make sure undefined is not being set to the list
      self.mediaFiles = {...self.mediaFiles, [experienceFieldName]:uuid}
    }

  },
  createMediaFileFromWidget: flow(function* f(experienceFieldName, mediaFileSelectState, groupSelectStore) {
    try {
      // Notify that upload is starting
      if (self.notifyUploadStatus) {
        self.notifyUploadStatus(true);
      }

      if (self.singleUseFile) {
        // Add timestamp to file name so you don't add the same file twice in the list
        self.updateMediaFile({name: addTimestampToFileName(self.name)});
      } else {
        const files = mediaFileSelectState?.store?.list || [];
        const dotIdx = self.name.lastIndexOf('.');
        const [base, ext] = dotIdx > 0 ? [self.name.slice(0, dotIdx), self.name.slice(dotIdx)] : [self.name, ''];
        let name = self.name, i = 1;
        while (files.some(f => f.name === name)) name = `${base} (${i++})${ext}`;
        if (name !== self.name) self.updateMediaFile({name});
      }

      const file = self.fileForS3Upload
      const fileFormat = getFileFormat(file)
      self.updateMediaFile({"fileFormat": fileFormat})
      new Notification()
          .setType("info")
          .setTitle(`File: ${self.name}`)
          .setMessage("Uploading image.")
          .send();
      const payload = {
        mediaFile: self,
        singleUseFile: self.singleUseFile,
        groupSelectStore: groupSelectStore,
        fileForS3Upload: self.fileForS3Upload,
        dimensionsMultiSelectStore: self.dimensionsMultiSelectStore,
        tagSelectStore: self.tagSelectStore,
        isExclusivelyGroupOwned: self.isExclusivelyGroupOwned,
      }
      const response = yield getEnv(self).mediaFileManager.createNewLocationUploadFile(payload);

      if (response instanceof Error) {
        throw response;
      }
      if (!response?.uuid) {
        throw new Error("Missing uuid in response: " + JSON.stringify(response));
      }
      self.mediaFileUUID = response.uuid;
      if (response.singleUseFile === true) {
        mediaFileSelectState.select(response, experienceFieldName);
      } else {
        yield mediaFileSelectState?.handleAddAndSelectNewMediaFile(response.uuid);
      }
      yield self.waitForMediaFileUpload(response.uuid, experienceFieldName, mediaFileSelectState);

      return response;
    } catch (error) {
      console.error("Failed to create mediaFile", error.message || error);
      const message =  error.message || error
      new Notification()
        .setType("error")
        .setTitle(``)
        .setMessage(message)
        .send();
      // Notify that upload failed (reset to false)
      if (self.notifyUploadStatus) {
        self.notifyUploadStatus(false);
      }
      return error;
    }
  }),
  /**
   * Detects if this is the first logo upload by checking if there's an existing logo
   * @param {string} experienceFieldName - The field name to check for existing logo
   * @returns {boolean} - True if this is the first logo upload, false if it's a replacement
   */
  isFirstLogoUpload(experienceFieldName) {
    if (experienceFieldName !== "mediaFileCode_logo") {
      return false;
    }
    // Check if there's already a logo in liveLinks
    // Note: On replacement uploads, liveLinks will contain the PREVIOUS logo URL
    // until updateSingleUseMediaFile() completes, which is why this check works
    const isFirst = !self.liveLinks?.original && !self.liveLinks?.thumbnail;
    console.log('[MediaFileModel] isFirstLogoUpload check:', {
      experienceFieldName,
      hasLiveLinks: !!self.liveLinks,
      hasOriginal: !!self.liveLinks?.original,
      hasThumbnail: !!self.liveLinks?.thumbnail,
      liveLinksValue: self.liveLinks,
      isFirstUpload: isFirst
    });
    return isFirst;
  },
  setUploadIntervalId(id) {
    self.uploadIntervalId = id;
  },
  setCancelUploads(value) {
    self.cancelUploads = value;
  },
  waitForMediaFileUpload: flow(function* (mediaFileUUID, experienceFieldName, mediaFileSelectState) {
    const isFirstUpload = self.isFirstLogoUpload(experienceFieldName);

    const fileReady = yield new Promise((resolve) => {
      let count = 0;
      const intervalId = setInterval(async () => {
        // Check if upload was cancelled
        if (self.cancelUploads) {
          clearInterval(intervalId);
          self.setUploadIntervalId(null);
          resolve(false);
          if (self.notifyUploadStatus) {
            self.notifyUploadStatus(false);
          }
          return;
        }

        count++;
        const response = await getEnv(self).mediaFileManager?.checkMediaFileStatus(mediaFileUUID);

        if (response.status === "UPLOADED") {
          clearInterval(intervalId);
          self.setUploadIntervalId(null);

          if (response.singleUseFile === true) {
            await self.updateSingleUseMediaFile(response, experienceFieldName, mediaFileSelectState);
          } else {
            self.updateMediaFile(response, experienceFieldName);
            const updatedFile = await getEnv(self).mediaFileManager?.getMediaFile(response.uuid);
            if (updatedFile && mediaFileSelectState?.store) {
              mediaFileSelectState.store.addFileToList(updatedFile.fileLocation);
            }
            mediaFileSelectState?.select(response, experienceFieldName);
          }
          if(experienceFieldName === "mediaFileCode_logo"){
            self.notifySelect("logo", response.liveLinks?.original)
          } else if(experienceFieldName === "mediaFileCode_background"){
            self.notifySelect("backgroundImage", response.liveLinks?.original)
          }

          // Notify that upload is complete
          if (self.notifyUploadStatus) {
            self.notifyUploadStatus(false);
          }

          resolve(true); // Resolve after all async work completes

        } else if (count === 100 || self.cancelUploads) {
          clearInterval(intervalId);
          self.setUploadIntervalId(null);
          resolve(false); // Resolve as "false" after 100 attempts or cancel
          // Notify that upload failed/timed out
          if (self.notifyUploadStatus) {
            self.notifyUploadStatus(false);
          }
        }
      }, 2000);
      self.setUploadIntervalId(intervalId);
    });
    return fileReady;
  }),
  updateSingleUseMediaFile: flow(function* (mediaFile,experienceFieldName, mediaFileSelectState) {
    // self.experienceData = '{}' // <== removes value from the widget so when item is selected updated values will render
    yield mediaFileSelectState?.updateSingleUseFile(mediaFile)
    mediaFileSelectState?.select({...mediaFile},experienceFieldName)
    self.liveLinks = mediaFileSelectState.selected.liveLinks
  }),
  removeMediaFile: flow(function* f(id) {
    if(id === "root_mediaFileCode_logo") {
      self.notifyDesignerDeleteKey("logo")
    } else if(id === "root_mediaFileCode_background") {
      self.notifyDesignerDeleteKey("backgroundImage")
    }
    self.mediaFileUUID = "";
    self.fileFormat = "";
    self.file = "";
    self.liveLinks = {};
    self.name = "";
    self.label = "";
    self.fileForS3Upload = {};
  }),
  setLiveLinks(liveLinks) {
    self.liveLinks = liveLinks
  },
  cancelMediaFileUpload() {
    self.setCancelUploads(true);
    if (self.uploadIntervalId) {
      clearInterval(self.uploadIntervalId);
      self.setUploadIntervalId(null);
    }
  },
  resetCancelFlag() {
    self.setCancelUploads(false);
  }
  })).views((self) => ({
    isUsageContent(){
        return self.usage === "content"
    }
}))
